import { useForm, useWatch } from "react-hook-form";
import {
  financialDetailsSchema,
  proposalDetailsSchema,
  unitDetailsSchema,
} from "../../utils/rental-financial-proposal/schemas";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import UnitDetailsForm from "../../components/documents-tab/rental-financial-proposal/forms/unit-details-form";
import FinancialDetailsForm from "../../components/documents-tab/rental-financial-proposal/forms/financial-details-form";
import ProposalDetailsForm from "../../components/documents-tab/rental-financial-proposal/forms/proposal-details-form";
import FinancialProposalPreview from "../../components/documents-tab/rental-financial-proposal/forms/preview";
import usePreviewProposal from "./preview-proposal.hook";
import { PreviewProposalDto } from "../../dtos/rental-financial-proposal";
import { ProposalType } from "../../types";
import PdfViewerModal from "../../components/documents-tab/rental-financial-proposal/pdf-viewer-modal";
import { mainSpecsOptions } from "../../constants";
import { FieldErrors, FieldValues } from "react-hook-form";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";

export type UnitDetailsFormFields = z.infer<typeof unitDetailsSchema>;
export type FinancialDetailsFormFields = z.infer<typeof financialDetailsSchema>;
export type ProposalDetailsFormFields = z.infer<typeof proposalDetailsSchema>;

interface DefaultValuesProps {
  unitDetails?: Partial<UnitDetailsFormFields>;
  financialDetails?: Partial<FinancialDetailsFormFields>;
  proposalDetails?: Partial<ProposalDetailsFormFields>;
}

interface FinancialProposalMultiStepFormProps {
  handleSubmit: (data: PreviewProposalDto) => void;
  defaultStep?: number;
  defaultValues: DefaultValuesProps;
}

function useFinancialProposalMultiStepForm({
  handleSubmit,
  defaultStep = 0,
  defaultValues,
}: FinancialProposalMultiStepFormProps) {
  const TOTAL_STEPS = 4;
  const [activeStep, setActiveStep] = useState<number>(defaultStep);
  const [previewOpen, setPreviewOpen] = useState(false);

  const nextStep = () => {
    setActiveStep((prev) => (TOTAL_STEPS - 1 === prev ? prev : prev + 1));
  };

  const previousStep = () => {
    setActiveStep((prev) => (prev === 0 ? prev : prev - 1));
  };
  const unitDetailsForm = useForm<UnitDetailsFormFields>({
    resolver: zodResolver(unitDetailsSchema),
    defaultValues: defaultValues.unitDetails,
  });

  const financialDetailsForm = useForm<FinancialDetailsFormFields>({
    resolver: zodResolver(financialDetailsSchema),
    defaultValues: {
      proposalType: ProposalType.Normal,
      ...defaultValues.financialDetails,
      annualIncreasePercent:
        defaultValues.financialDetails?.annualIncreasePercent ?? 10,
    },
  });

  const proposalDetailsForm = useForm<ProposalDetailsFormFields>({
    resolver: zodResolver(proposalDetailsSchema),
    defaultValues: defaultValues.proposalDetails ?? {
      mainSpecs: mainSpecsOptions,
    },
  });

  const [finishingCost, furnishingCost] = [
    Number(
      useWatch({ control: unitDetailsForm.control, name: "finishingCost" }),
    ) || 0,
    Number(
      useWatch({ control: unitDetailsForm.control, name: "furnishingCost" }),
    ) || 0,
  ];
  const totalAmount = finishingCost + furnishingCost;

  const preview = usePreviewProposal();

  const validateForms = async (): Promise<boolean> => {
    const [unitValid, financialValid, proposalValid] = await Promise.all([
      unitDetailsForm.trigger(),
      financialDetailsForm.trigger(),
      proposalDetailsForm.trigger(),
    ]);
    if (!unitValid || !financialValid || !proposalValid) {
      if (!unitValid) setActiveStep(0);
      else if (!financialValid) setActiveStep(1);
      else if (!proposalValid) setActiveStep(2);
      return false;
    }
    return true;
  };

  const buildSubmitPayload = () => {
    const financialValues = financialDetailsForm.getValues();
    const isNawyNow = financialValues.proposalType === ProposalType.NawyNow;
    return {
      ...unitDetailsForm.getValues(),
      ...financialValues,
      ...proposalDetailsForm.getValues(),
      ...(isNawyNow
        ? {
            paymentPlan: undefined,
            loanAmount: financialValues.loanAmount,
            loanTerm: financialValues.loanTerm,
            monthlyInstallment: financialValues.monthlyInstallment,
          }
        : {}),
    };
  };

  const notifyInvalid = (errors: FieldErrors<FieldValues>) => {
    const firstMessage = Object.values(errors)
      .map((error) => error?.root?.message ?? error?.message)
      .find(Boolean);
    if (!firstMessage) return;
    showToast({
      type: ToastType.ERROR,
      message: String(firstMessage),
      toastId: TOAST_IDS.GENERIC_ERROR,
    });
  };

  const triggerPreview = async () => {
    if (!(await validateForms())) return;
    preview.mutate(buildSubmitPayload(), {
      onSuccess: () => {
        setPreviewOpen(true);
      },
    });
  };
  const steps = useMemo(
    () => [
      {
        value: 0,
        title: "Unit",
        onNext: unitDetailsForm.handleSubmit(() => nextStep(), notifyInvalid),
        content: <UnitDetailsForm form={unitDetailsForm} />,
      },
      {
        value: 1,
        title: "Financials",
        onNext: financialDetailsForm.handleSubmit(
          () => nextStep(),
          notifyInvalid,
        ),
        content: (
          <FinancialDetailsForm
            form={financialDetailsForm}
            totalAmount={totalAmount}
          />
        ),
      },
      {
        value: 2,
        title: "Proposal",
        onNext: proposalDetailsForm.handleSubmit(() => {
          nextStep();
        }, notifyInvalid),
        content: <ProposalDetailsForm form={proposalDetailsForm} />,
      },
      {
        value: 3,
        title: "Preview",
        onNext: async () => {
          if (!(await validateForms())) return;
          handleSubmit(buildSubmitPayload());
        },
        content: (
          <FinancialProposalPreview
            unitDetailsForm={unitDetailsForm}
            financialDetailsForm={financialDetailsForm}
          />
        ),
      },
    ],
    [
      unitDetailsForm,
      financialDetailsForm,
      proposalDetailsForm,
      totalAmount,
      handleSubmit,
    ],
  );
  const currentStep = steps.find((s) => s.value === activeStep) ?? steps[0];

  const reset = (defaultValues?: DefaultValuesProps) => {
    unitDetailsForm.reset(defaultValues?.unitDetails);
    financialDetailsForm.reset({
      proposalType: ProposalType.Normal,
      ...defaultValues?.financialDetails,
      annualIncreasePercent:
        defaultValues?.financialDetails?.annualIncreasePercent ?? 10,
    });
    proposalDetailsForm.reset(defaultValues?.proposalDetails);
  };

  const PreviewModal = () => (
    <PdfViewerModal
      open={previewOpen}
      setOpen={setPreviewOpen}
      headerText="Preview"
      source={preview.data?.data}
    />
  );

  const clearForms = () => {
    setActiveStep(defaultStep);
    setPreviewOpen(false);
    reset();
  };

  return {
    activeStep,
    steps,
    nextStep,
    previousStep,
    currentStep,
    isLastStep: activeStep === steps[steps.length - 1].value,
    reset,
    clearForms,
    forms: {
      unitDetails: unitDetailsForm,
      financialDetails: financialDetailsForm,
      proposalDetails: proposalDetailsForm,
    },
    triggerPreview,
    previewLoading: preview.isPending,
    PreviewModal,
  };
}

export default useFinancialProposalMultiStepForm;
